// The Subscription section in Settings: what an arbiter pays for their account.
//
// All of the decisions are the server's (functions/lib/billing.js): whether
// subscriptions are open yet, whether this account may subscribe, which plans
// exist and what they cost. This file asks billingApi for that and draws it.
// Paying and managing a subscription both happen on pages Stripe hosts, so the
// card number never touches Zugzwang — the buttons here only fetch the address
// of the right Stripe page and go there.
//
// Hidden unless the server says there is something to show: before launch that
// is only for administrators (who test the flow in Stripe test mode), so an
// ordinary arbiter sees nothing at all until the switch is turned on.

import { callFunction, currentLang } from '../services/functions-api.js';
import { renderWithdrawalInfo } from './withdrawal-info.js';
import { SELLER } from './seller.js';
import { openPlanChoice } from './plan-choice.js';
import { osloDate } from '../utils/oslo-date.js';

const tr = (s, p) => (window.tr ? window.tr(s, p) : s);

/** Statuses where there IS a subscription, to be managed rather than bought again. */
const EXISTING = new Set(['trialing', 'active', 'past_due', 'unpaid', 'paused']);

const PLAN_LABELS = {
    monthly: { name: 'Monthly', per: '{price} kr / month' },
    quarterly: { name: 'Every 3 months', per: '{price} kr / 3 months' },
    yearly: { name: 'Yearly', per: '{price} kr / year' }
};

const DAY_MS = 24 * 60 * 60 * 1000;

function formatKroner(ore) {
    const kr = ore / 100;
    return Number.isInteger(kr) ? String(kr) : kr.toFixed(2).replace('.', ',');
}

// The day in Norway — the e-mails (functions/lib/billing-emails.js) state these
// dates in Europe/Oslo, and the app must not name a different day for the same
// trial end or withdrawal deadline.
function formatDay(ms) {
    if (!ms) return '';
    const iso = osloDate(ms);
    return window.trDate ? window.trDate(iso) : iso;
}

function planName(id) {
    return PLAN_LABELS[id] ? tr(PLAN_LABELS[id].name) : '';
}

/** One sentence on where this account stands. */
function statusText(b) {
    const a = b.access || {};
    switch (a.reason) {
        case 'complimentary':
            // A time-limited access code says until when; anything else is for good.
            return a.untilMs
                ? tr('Your arbiter account is free until {date}. To carry on after that, choose a plan below.', { date: formatDay(a.untilMs) })
                : tr('Your arbiter account is free of charge.');
        case 'trial':
            return tr('Free trial until {date}. Your plan ({plan}) starts after that.', { date: formatDay(a.untilMs), plan: planName(b.plan) });
        case 'subscribed':
            return a.renews
                ? tr('{plan} plan. Renews on {date}.', { plan: planName(b.plan), date: formatDay(a.untilMs) })
                : tr('{plan} plan. Ends on {date} and will not renew.', { plan: planName(b.plan), date: formatDay(a.untilMs) });
        case 'past_due':
            return tr('Your last payment did not go through. Update your card to keep your subscription.');
        case 'inactive':
            return EXISTING.has(b.status)
                ? tr('Your subscription is on hold. Open it to continue.')
                : tr('Your subscription has ended. Choose a plan to subscribe again.');
        default:
            // A player sees this section as the way to become an arbiter.
            if (!b.isArbiter) {
                return b.trialUsed
                    ? tr('Want to run tournaments? Choose a plan to get an arbiter account.')
                    : tr('Want to run tournaments? Choose a plan to get an arbiter account. Every plan starts with {n} days free.', { n: b.trialDays });
            }
            return b.trialUsed
                ? tr('Choose how you want to pay for your arbiter account.')
                : tr('Choose how you want to pay for your arbiter account. Every plan starts with {n} days free.', { n: b.trialDays });
    }
}

function renderPlans(container, b) {
    container.replaceChildren();
    for (const plan of b.plans || []) {
        const label = PLAN_LABELS[plan.id];
        if (!label) continue;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn-outline';
        btn.style.cssText = 'display: flex; flex-direction: column; align-items: flex-start; gap: 0.25rem; padding: 0.9rem 1rem; text-align: left;';

        const name = document.createElement('strong');
        name.textContent = tr(label.name);
        const price = document.createElement('span');
        price.textContent = tr(label.per, { price: formatKroner(plan.amount) });
        btn.append(name, price);
        if (!b.trialUsed) {
            const trial = document.createElement('span');
            trial.style.cssText = 'font-size: 0.8rem; color: var(--text-muted);';
            trial.textContent = tr('{n} days free', { n: b.trialDays });
            btn.append(trial);
        }

        btn.addEventListener('click', () => goToStripe(btn, { action: 'checkout', plan: plan.id, startRequested: startNowChecked() }));
        container.append(btn);
    }
    syncPlanButtons(b);
}

function startNowChecked() {
    const box = document.getElementById('subscription-start-now');
    return !!(box && box.checked);
}

/**
 * With the trial spent, the paid service starts inside the withdrawal period,
 * which the law lets us charge for only if the arbiter asked for it in so many
 * words (angrerettloven § 19). Until the box is ticked there is nothing to buy.
 */
function syncPlanButtons(b) {
    const locked = !!b.trialUsed && !startNowChecked();
    for (const btn of document.querySelectorAll('#subscription-plans button')) btn.disabled = locked;
}

/** Price, renewal and the right of withdrawal, said before anything is bought. */
function renderTerms(b, canBuy) {
    const terms = document.getElementById('subscription-terms');
    terms.style.display = canBuy ? 'block' : 'none';
    if (!canBuy) return;

    document.getElementById('subscription-terms-summary').textContent = b.trialUsed
        ? tr('The subscription renews automatically until you cancel. You can withdraw within {n} days of subscribing (angrerett).', { n: b.withdrawalDays })
        : tr('After the free trial the subscription renews automatically until you cancel. You can withdraw within {n} days of subscribing (angrerett) and then pay nothing.', { n: b.withdrawalDays });

    const row = document.getElementById('subscription-start-now-row');
    row.style.display = b.trialUsed ? 'flex' : 'none';
    document.getElementById('subscription-start-now-text').textContent =
        tr('Start my subscription straight away. I understand that if I withdraw within {n} days, I pay for the days I have used.', { n: b.withdrawalDays });
    document.getElementById('subscription-start-now').onchange = () => syncPlanButtons(b);
}

/** The "Withdraw from purchase" box, for as long as the right of withdrawal lasts. */
function renderWithdraw(b) {
    const row = document.getElementById('subscription-withdraw-row');
    const w = b.withdrawal || {};
    row.style.display = w.available ? 'block' : 'none';
    if (!w.available) return;

    // The window closes a day after the legal deadline (see WITHDRAWAL_DAYS in
    // functions/lib/billing.js); the date shown is the legal one.
    const until = formatDay(w.deadlineMs - DAY_MS);
    document.getElementById('subscription-withdraw-text').textContent = b.status === 'trialing'
        ? tr('You can withdraw from your purchase until {date} (angrerett). You have not been charged, and will not be.', { date: until })
        : tr('You can withdraw from your purchase until {date} (angrerett). You get your money back, less the days used if you asked for the subscription to start straight away.', { date: until });
}

async function withdrawFromPurchase(button) {
    const sure = window.Dialog
        ? await window.Dialog.confirm(
            tr('Your subscription ends straight away and cannot be resumed. A receipt is sent to your email.'),
            tr('Withdraw from purchase'),
            tr('Withdraw'),
            true)
        : confirm(tr('Withdraw from purchase'));
    if (!sure) return;

    button.disabled = true;
    const res = await callFunction('billingApi', { action: 'withdraw', lang: currentLang() });
    button.disabled = false;

    if (res.ok) {
        const message = res.refund > 0
            ? tr('You have withdrawn from your purchase. {amount} kr is being refunded to the card you paid with.', { amount: formatKroner(res.refund) })
            : tr('You have withdrawn from your purchase. Nothing has been charged.');
        if (window.Dialog) window.Dialog.success(message, tr('Withdraw from purchase'));
        await refreshSubscription();
        return;
    }

    const code = res.error && typeof res.error === 'object' ? res.error.code : res.error;
    const message = code === 'withdrawal_closed'
        ? tr('The 14 days to withdraw have passed. You can still cancel under Manage Subscription.')
        : tr('Could not withdraw right now. Please try again shortly, or write to {email}.', { email: SELLER.email });
    window.Dialog ? window.Dialog.error(message) : alert(message);
    await refreshSubscription();
}

/** Ask billingApi for a Stripe page and go there. */
async function goToStripe(button, body) {
    button.disabled = true;
    const res = await callFunction('billingApi', Object.assign({ lang: currentLang() }, body));
    if (res.ok && res.url) {
        window.location.assign(res.url);
        return;
    }
    button.disabled = false;

    const code = res.error && typeof res.error === 'object' ? res.error.code : res.error;
    if (code === 'already_subscribed') {
        await refreshSubscription();
        return;
    }
    let message = tr('Could not reach the payment page. Please try again shortly.');
    if (code === 'billing_closed') message = tr('Subscriptions are not open yet.');
    if (code === 'start_not_requested') message = tr('Tick the box to start your subscription straight away first.');
    window.Dialog ? window.Dialog.error(message) : alert(message);
}

let loading = null;

// The last status billingApi gave: undefined until it has answered once, null
// when it had nothing to say. The tour (js/ui/tour.js) reads it, so it does not
// offer itself to someone who is about to be shown "Complete your subscription".
let lastStatus;

/** What billingApi last said about this account, without asking again. */
export function lastBillingStatus() {
    return lastStatus;
}

/** Fetch this account's billing status and redraw the section. */
export function refreshSubscription() {
    if (loading) return loading;
    loading = (async () => {
        const section = document.getElementById('settings-subscription-section');
        if (!section) return;

        const res = await callFunction('billingApi', { action: 'status' });
        const b = res.ok && res.billing;
        lastStatus = b || null;
        // Not an arbiter, not signed in, not open yet, or the call failed: say
        // nothing rather than show a half-drawn section.
        if (!b || (!b.canSubscribe && !b.hasCustomer)) {
            section.style.display = 'none';
            return;
        }

        document.getElementById('subscription-preview-note').style.display = b.open ? 'none' : 'block';
        document.getElementById('subscription-status').textContent = statusText(b);

        const plans = document.getElementById('subscription-plans');
        // The server decides (offerPlans): nothing to buy for someone already
        // subscribed, or for an arbiter who is free for good.
        const canBuy = b.offerPlans !== undefined ? !!b.offerPlans : (b.canSubscribe && !EXISTING.has(b.status));
        renderTerms(b, canBuy);
        plans.style.display = canBuy ? 'grid' : 'none';
        if (canBuy) renderPlans(plans, b);
        renderWithdraw(b);
        renderWithdrawalInfo(document.getElementById('subscription-withdrawal-body'));

        document.getElementById('subscription-manage-row').style.display = b.hasCustomer ? 'flex' : 'none';
        section.style.display = 'block';

        if (justPaid && !b.isArbiter) {
            justPaid = false;
            waitForArbiterRole();
        }
        offerPendingPlan(b, canBuy);
    })().finally(() => { loading = null; });
    return loading;
}

let offeredPendingPlan = false;

/** Set on coming back from a completed Stripe Checkout (handleReturnFromStripe). */
let justPaid = false;

/**
 * Someone who chose a plan when they signed up is offered it again on their
 * first confirmed sign-in — "Complete your subscription", on to Stripe. Once
 * per page load: closing it leaves the same plans here in Settings.
 */
function offerPendingPlan(b, canBuy) {
    // Not on the way back from paying: the webhook may not have caught up yet.
    if (offeredPendingPlan || justPaid || !canBuy || !b.pendingPlan) return;
    offeredPendingPlan = true;
    openPlanChoice({ mode: 'checkout', selected: b.pendingPlan, trialUsed: !!b.trialUsed });
}

const ROLE_WAIT_TRIES = 8;
const ROLE_WAIT_MS = 2500;

/**
 * Back from Stripe as a player who has just bought an arbiter account: the
 * webhook grants the role a moment after the payment. Refresh the token until it carries
 * it, then reload, which draws the arbiter's app. Gives up quietly — the next
 * sign-in picks the role up anyway.
 */
async function waitForArbiterRole() {
    if (!window.authApp || typeof window.authApp.refreshRole !== 'function') return;
    for (let i = 0; i < ROLE_WAIT_TRIES; i++) {
        await new Promise(resolve => setTimeout(resolve, ROLE_WAIT_MS));
        let role = '';
        try { role = await window.authApp.refreshRole(); } catch (e) { /* try again */ }
        if (role === 'organizer') {
            window.location.reload();
            return;
        }
    }
}

/**
 * Coming back from Stripe: `?billing=success|cancelled|portal` on the URL.
 * Stripe's webhook may land a moment after the arbiter does, so a successful
 * checkout re-reads the status once more a few seconds later.
 */
function handleReturnFromStripe() {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('billing');
    if (!outcome) return;

    params.delete('billing');
    const query = params.toString();
    history.replaceState(history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);

    if (outcome === 'success') {
        // Whether this account is becoming an arbiter is known only once the
        // status arrives (refreshSubscription), which then waits for the role.
        justPaid = true;
        const note = tr('Thank you! Your subscription is being set up and will show in Settings in a moment.');
        if (window.Dialog) window.Dialog.success(note, tr('Subscription'));
        setTimeout(refreshSubscription, 5000);
    }
}

export function setupSubscription() {
    handleReturnFromStripe();

    const manage = document.getElementById('btn-manage-subscription');
    if (manage) manage.addEventListener('click', () => goToStripe(manage, { action: 'portal' }));
    const withdrawBtn = document.getElementById('btn-withdraw-subscription');
    if (withdrawBtn) withdrawBtn.addEventListener('click', () => withdrawFromPurchase(withdrawBtn));

    // Sign-out reloads the page, so signing in is the only moment this changes.
    document.addEventListener('zw:arbiter-signed-in', refreshSubscription);
    if (window.__zwArbiterSignedIn) refreshSubscription();
}
